import { useState, useEffect, useMemo, useRef } from 'react'
import { useTranslation } from 'react-i18next'
import {
  FormSection,
  Button,
  Input,
  ConfirmationModal,
  LoadingSpinner,
  ErrorState,
  useToastActions,
} from '@airclou/ui'
import { CheckCircle2, Clock, Check, X, Sparkles } from 'lucide-react'
import { useAuth } from '../../../contexts/AuthContext'
import { canSeeBilling } from '../../../navigation'
import { RoleUnknownNotice } from '../../../components/state/RoleUnknownNotice'
import { SubscribeModal } from '../components/SubscribeModal'
import { CheckoutReturn, type CheckoutOutcome } from '../components/CheckoutReturn'
import { waitForPlanChange } from '../../../services/billing'
import { CONTACT_SALES_URL } from '../../../config'
import {
  getPlans,
  getMyPlan,
  changePlan,
  startSuiteTrial,
  changeSeats,
  TIER_ORDER,
  isPaying,
} from '../../../services/plans'
import type { Plan, MyPlan, SuitePlan } from '../../../services/plans'

/**
 * Plans, one card per suite (docs/plans/free-plan-and-reverse-trial-plan.md §5).
 *
 * A suite's tier comes from a live subscription, a running trial or
 * extension, or its free plan. Every suite has the same four tiers; what they
 * contain is the suite's own. Buying a tier goes through Stripe Checkout, and
 * changing a live subscription's tier goes through Stripe too — the page never
 * sets a plan itself, because only Stripe's webhook records what was bought.
 */

/** Features shown in the comparison, in this order; the rest follow alphabetically. */
const FEATURE_ORDER = [
  'ai_drafts',
  'ai_agent',
  'api_access',
  'max_users',
  'max_workspaces',
  'max_brands',
  'max_mailboxes',
  'storage_limit_gb',
  'custom_branding',
  'chat_file_upload_enabled',
  'audit_log_retention_days',
  'audit_log_export',
  'sso',
]

type T = (key: string, options?: Record<string, unknown>) => string

function formatPrice(priceCents: number): string {
  return `$${(priceCents / 100).toFixed(0)}`
}

function formatFeatureValue(value: any, t: T): string {
  if (typeof value === 'boolean') return value ? t('billingPlans.yes') : t('billingPlans.no')
  if (typeof value === 'number') return value.toLocaleString()
  if (typeof value === 'string') return value === 'unlimited' ? t('billingPlans.unlimited') : value
  return JSON.stringify(value)
}

function priceLabel(plan: Plan, t: T): string {
  if (plan.tier === 'free') return t('billingPlans.free')
  if (plan.tier === 'enterprise') return t('billingPlans.contactUs')
  return t('billingPlans.perSeatMonth', { price: formatPrice(plan.priceCents) })
}

export function SubscriptionTab() {
  const { t } = useTranslation('dashboard')
  const { user } = useAuth()
  const { success: toastSuccess, error: toastError } = useToastActions()

  const [plans, setPlans] = useState<Plan[]>([])
  const [myPlan, setMyPlan] = useState<MyPlan | null>(null)
  const [isLoading, setIsLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)

  // A tier change for a live subscription, waiting on confirmation.
  const [confirmPlan, setConfirmPlan] = useState<Plan | null>(null)
  const [isChanging, setIsChanging] = useState(false)
  // A tier change Stripe has accepted, waiting for the webhook to record it.
  // Only the webhook writes what is bought, so until it lands a plain refetch
  // shows the old plan — and a second click would be refused as "already on
  // that plan". `slow`: the wait ran out; the change is not lost.
  const [pendingSwitch, setPendingSwitch] = useState<PendingSwitch | null>(null)
  const pendingSwitchAbort = useRef<AbortController | null>(null)
  useEffect(() => () => pendingSwitchAbort.current?.abort(), [])
  // Which plan the customer is buying, if the subscribe modal is open.
  const [subscribePlan, setSubscribePlan] = useState<Plan | null>(null)
  const [startingTrial, setStartingTrial] = useState<string | null>(null)
  // Set when Stripe has just redirected the browser back to us.
  const [checkout, setCheckout] = useState<{ outcome: CheckoutOutcome; suite?: string } | null>(() => {
    const params = new URLSearchParams(window.location.search)
    const param = params.get('checkout')
    return param === 'complete' || param === 'cancelled'
      ? { outcome: param, suite: params.get('suite') ?? undefined }
      : null
  })

  // The org role, which is what the billing routes resolve.
  const canBuy = canSeeBilling(user?.orgRole ?? undefined)
  // A known role that cannot buy: say who can (T5). An unknown role is a
  // different claim, and RoleUnknownNotice makes it.
  const askAdmin = !canBuy && user?.orgRole !== undefined

  /** Drop ?checkout=... so a refresh doesn't replay the return banner. */
  function clearCheckoutParam() {
    setCheckout(null)
    const url = new URL(window.location.href)
    url.searchParams.delete('checkout')
    url.searchParams.delete('suite')
    window.history.replaceState({}, '', url.toString())
  }

  const plansBySuite = useMemo(() => {
    const out = new Map<string, Plan[]>()
    for (const plan of plans) {
      const list = out.get(plan.suite) ?? []
      list.push(plan)
      out.set(plan.suite, list)
    }
    for (const list of out.values()) {
      list.sort((a, b) => TIER_ORDER.indexOf(a.tier) - TIER_ORDER.indexOf(b.tier))
    }
    return out
  }, [plans])

  useEffect(() => {
    loadData()
  }, [])

  async function loadData() {
    setIsLoading(true)
    setError(null)
    try {
      const [plansData, myPlanData] = await Promise.all([getPlans(), getMyPlan()])
      setPlans(plansData)
      setMyPlan(myPlanData)
    } catch (err: any) {
      console.error('Failed to load plan data:', err)
      setError(err.message || t('billingPlans.loadFailed'))
    } finally {
      setIsLoading(false)
    }
  }

  async function handleStartTrial(suite: SuitePlan) {
    setStartingTrial(suite.suite)
    try {
      await startSuiteTrial(suite.suite)
      toastSuccess(t('billingPlans.trialStarted', { suite: suite.suiteName }))
      await loadData()
    } catch (err: any) {
      toastError(err?.message || t('billingPlans.trialStartFailed'))
    } finally {
      setStartingTrial(null)
    }
  }

  async function handleChangeSeats(suite: string, seats: number) {
    try {
      const result = await changeSeats(suite, seats)
      toastSuccess(t('billingPlans.seatsUpdated', { count: result.seats }))
      // Seats are read live from Stripe, so the reload shows the new quantity at once.
      await loadData()
    } catch (err: any) {
      // The server's own words: SEATS_IN_USE names how many people to remove first.
      toastError(err?.message || t('billingPlans.seatsChangeFailed'))
    }
  }

  async function handleConfirmChange() {
    if (!confirmPlan) return
    setIsChanging(true)
    const plan = confirmPlan
    try {
      await changePlan(plan.slug)
    } catch (err: any) {
      console.error('Failed to change plan:', err)
      toastError(err.message || t('billingPlans.changeFailed'))
      setIsChanging(false)
      return
    }
    setIsChanging(false)
    setConfirmPlan(null)

    // Stripe has the change; our record follows when its webhook lands. The
    // card says so until the suite's status names the new plan, the same wait
    // the checkout return does.
    setPendingSwitch({ suite: plan.suite, plan, slow: false })
    pendingSwitchAbort.current?.abort()
    const controller = new AbortController()
    pendingSwitchAbort.current = controller
    const outcome = await waitForPlanChange({ suite: plan.suite, planSlug: plan.slug, signal: controller.signal })
    if (controller.signal.aborted) return
    if (outcome === 'changed') {
      await loadData()
      setPendingSwitch(null)
      toastSuccess(t('billingPlans.switched', { plan: plan.name }))
    } else {
      // Not an error: the change is in Stripe, only our record lags. Re-read
      // in case it landed between the last poll and now, and say it is slow.
      await loadData()
      setPendingSwitch((current) => (current?.plan.slug === plan.slug ? { ...current, slow: true } : current))
    }
  }

  // One stable tree, with only the DATA region swapping between spinner, error
  // and content — the "minimal dynamic UI" rule, and here it is load-bearing.
  //
  // The checkout-return banner depends on the URL, not on plans/my-plan, so it
  // belongs above the gate. When it sat inside, the early load gate unmounted
  // it on every refetch — and a SUCCESSFUL poll calls onActivated -> loadData
  // -> setIsLoading(true), so it remounted with fresh state and polled again:
  // an infinite loop found on the first real payment (2026-08-31).
  return (
    <>
      <div className="space-y-6">
        {checkout && (
          <CheckoutReturn
            outcome={checkout.outcome}
            suite={checkout.suite}
            onActivated={loadData}
            onDismiss={clearCheckoutParam}
          />
        )}
        {isLoading ? (
          <div className="flex items-center justify-center py-12">
            <LoadingSpinner />
          </div>
        ) : error ? (
          <ErrorState
            title={t('billingPlans.loadFailed')}
            description={error}
            action={<Button variant="outline" onClick={loadData}>{t('billingPlans.retry')}</Button>}
          />
        ) : (
          (myPlan?.suites ?? []).map((suite) => (
            <SuiteCard
              key={suite.suite}
              suite={suite}
              plans={plansBySuite.get(suite.suite) ?? []}
              canBuy={canBuy}
              askAdmin={askAdmin}
              startingTrial={startingTrial === suite.suite}
              pendingSwitch={pendingSwitch?.suite === suite.suite ? pendingSwitch : null}
              onDismissPendingSwitch={() => setPendingSwitch(null)}
              onStartTrial={() => handleStartTrial(suite)}
              onSubscribe={setSubscribePlan}
              onChange={setConfirmPlan}
              onChangeSeats={handleChangeSeats}
              t={t}
            />
          ))
        )}
      </div>

      {confirmPlan && (
        <ConfirmationModal
          isOpen={!!confirmPlan}
          onClose={() => setConfirmPlan(null)}
          title={t('billingPlans.confirmSwitchTitle', { plan: confirmPlan.name })}
          description={t('billingPlans.confirmSwitchBody', { plan: confirmPlan.name, price: priceLabel(confirmPlan, t) })}
          variant="info"
          confirmText={t('billing.confirmChange')}
          cancelText={t('common.cancel')}
          onConfirm={handleConfirmChange}
          onCancel={() => setConfirmPlan(null)}
          isLoading={isChanging}
        />
      )}

      {subscribePlan && (
        <SubscribeModal
          plan={subscribePlan}
          // Never prefill the 'Unknown Organization' placeholder AuthContext
          // falls back to — it would end up printed on a real invoice.
          defaultOrganizationName={
            user?.tenantName && user.tenantName !== 'Unknown Organization' ? user.tenantName : ''
          }
          defaultBillingEmail={user?.email || ''}
          isReactivating={
            myPlan?.suites.find((b) => b.suite === subscribePlan.suite)?.subscription?.ended === true
          }
          onClose={() => setSubscribePlan(null)}
        />
      )}
    </>
  )
}

interface PendingSwitch {
  suite: string
  plan: Plan
  /** The wait for the webhook ran out. The change is in Stripe; our record lags. */
  slow: boolean
}

interface SuiteCardProps {
  suite: SuitePlan
  plans: Plan[]
  canBuy: boolean
  /** The role is known and cannot buy: the card says who can instead of offering nothing. */
  askAdmin: boolean
  startingTrial: boolean
  /** A tier change Stripe accepted that the webhook has not recorded yet. */
  pendingSwitch: PendingSwitch | null
  onDismissPendingSwitch: () => void
  onStartTrial: () => void
  onSubscribe: (plan: Plan) => void
  onChange: (plan: Plan) => void
  /** Set the seats bought on the live subscription; resolves once the page has reloaded. */
  onChangeSeats: (suite: string, seats: number) => Promise<void>
  t: T
}

/**
 * The seat meter and, for a paying suite, the control that changes the seats
 * bought (ai-allowance-and-seat-caps-plan.md §4.5). The meter reads the two
 * counts my-plan carries; the control posts the new quantity and lets the parent
 * reload, so the meter and the subscription's seats both come from the server.
 */
function SeatMeter({
  suite,
  paying,
  canBuy,
  onChangeSeats,
  t,
}: {
  suite: SuitePlan
  paying: boolean
  canBuy: boolean
  onChangeSeats: (suite: string, seats: number) => Promise<void>
  t: T
}) {
  const [draft, setDraft] = useState<string | null>(null)
  const [saving, setSaving] = useState(false)
  if (suite.seatsUsed === undefined) return null
  const used = suite.seatsUsed
  const bought = suite.seatsBought ?? null
  const label =
    bought === null
      ? t('billingPlans.seatsMeterUnlimited', { used })
      : suite.seatSource === 'free'
        ? t('billingPlans.seatsMeterFree', { used, bought })
        : t('billingPlans.seatsMeter', { used, bought })
  const seats = Number(draft)
  const seatsValid = Number.isInteger(seats) && seats >= 1 && seats <= 1000
  return (
    <div className="flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-between" data-testid="seat-meter">
      <p className="text-sm text-muted-foreground">{label}</p>
      {paying && canBuy && bought !== null && (
        draft === null ? (
          <Button variant="outline" size="sm" onClick={() => setDraft(String(bought))}>
            {t('billingPlans.addSeats')}
          </Button>
        ) : (
          <form
            className="flex items-center gap-2"
            onSubmit={async (e) => {
              e.preventDefault()
              if (!seatsValid) return
              setSaving(true)
              try {
                await onChangeSeats(suite.suite, seats)
                setDraft(null)
              } finally {
                setSaving(false)
              }
            }}
          >
            <label htmlFor={`seats-${suite.suite}`} className="text-sm text-foreground">
              {t('billingPlans.seatsLabel')}
            </label>
            <Input
              id={`seats-${suite.suite}`}
              type="number"
              min={1}
              max={1000}
              step={1}
              value={draft}
              onChange={(value: string) => setDraft(value)}
              disabled={saving}
              className="w-24"
            />
            <Button type="submit" size="sm" disabled={!seatsValid || saving || seats === bought}>
              {t('billingPlans.seatsSave')}
            </Button>
            <Button type="button" variant="outline" size="sm" disabled={saving} onClick={() => setDraft(null)}>
              {t('common.cancel')}
            </Button>
          </form>
        )
      )}
    </div>
  )
}

/**
 * The AI agent credit meter and, when the pool is used, the pause notice
 * (ai-allowance-and-seat-caps-plan.md §4.3). Hidden for a tier with no AI agent
 * (an allowance of 0); an unlimited pool shows the count alone.
 */
function CreditMeter({ suite, t }: { suite: SuitePlan; t: T }) {
  const allowance = suite.creditsAllowance
  if (allowance === undefined || allowance === 0) return null
  const used = suite.creditsUsed ?? null
  const date = suite.creditsResetAt
    ? new Date(suite.creditsResetAt).toLocaleDateString(undefined, { month: 'long', day: 'numeric', timeZone: 'UTC' })
    : ''
  const label =
    allowance === null
      ? used === null
        ? null
        : t('billingPlans.creditsMeterUnlimited', { used: used.toLocaleString() })
      : used === null
        ? t('billingPlans.creditsMeterNoCount', { allowance: allowance.toLocaleString(), date })
        : t('billingPlans.creditsMeter', { used: used.toLocaleString(), allowance: allowance.toLocaleString(), date })
  return (
    <div className="space-y-2" data-testid="credit-meter">
      {label && <p className="text-sm text-muted-foreground">{label}</p>}
      {suite.creditsPaused && allowance !== null && (
        <p
          className="rounded-lg border border-amber-200 bg-amber-50 p-3 text-sm text-amber-800 dark:border-amber-800 dark:bg-amber-900/20 dark:text-amber-200"
          role="status"
        >
          {t('billingPlans.creditsPaused', { allowance: allowance.toLocaleString(), date })}
          {suite.creditsPerSeatAdded
            ? ` ${t('billingPlans.creditsPerSeat', { perSeat: suite.creditsPerSeatAdded.toLocaleString() })}`
            : ''}
        </p>
      )}
    </div>
  )
}

function SuiteCard({
  suite,
  plans,
  canBuy,
  askAdmin,
  startingTrial,
  pendingSwitch,
  onDismissPendingSwitch,
  onStartTrial,
  onSubscribe,
  onChange,
  onChangeSeats,
  t,
}: SuiteCardProps) {
  const currentPlanLabel = t('billing.currentPlan')
  const tierLabel = (tier: string) => t(`billingPlans.tier_${tier}`)
  const subscription = suite.subscription
  // Paying right now: a subscription Stripe still bills, read from the
  // subscription itself — a Starter bought during a Pro trial is paid for
  // while the trial still sets the tier. A cancelled one keeps billing until
  // its period ends, so it still counts here.
  const paying = isPaying(suite)
  const onGrant = suite.grant?.live === true
  // Which column is "yours": what you pay for, or free. A trial is never a
  // current plan — its Pro column is the one to buy.
  const currentTier = paying ? subscription!.tier : suite.source === 'free' ? 'free' : null
  // Switching a live subscription needs to read it from Stripe; when that
  // failed, offer no switch rather than one that would fail. Subscribing does
  // not need it, so a suite whose subscription ended can still buy again.
  const stripeDown = suite.billingProviderUnavailable

  const featureKeys = useMemo(() => {
    const keys = new Set<string>()
    for (const plan of plans) for (const f of plan.features) keys.add(f.featureKey)
    const ordered = FEATURE_ORDER.filter((k) => keys.has(k))
    const remaining = [...keys].filter((k) => !FEATURE_ORDER.includes(k)).sort()
    return [...ordered, ...remaining]
  }, [plans])

  const valueOf = (plan: Plan, key: string) => plan.features.find((f) => f.featureKey === key)?.value ?? null

  function action(plan: Plan) {
    if (plan.tier === currentTier) {
      return (
        <Button variant="outline" size="sm" disabled>
          {currentPlanLabel}
        </Button>
      )
    }
    if (plan.tier === 'free') return null
    if (plan.tier === 'enterprise') {
      return (
        <a
          href={CONTACT_SALES_URL}
          target="_blank"
          rel="noreferrer"
          className="text-sm font-medium text-primary underline"
        >
          {t('billingPlans.contactUs')}
        </a>
      )
    }
    if (!canBuy) return null
    if (paying) {
      // While a switch waits on the webhook, a second one would either be
      // refused ("already on that plan") or race the first; the pending line
      // below says what is happening. A subscription set to end at the period
      // end gets no Switch either: the change would be charged now for a plan
      // that ends on the same date (the server refuses it too).
      return stripeDown || subscription?.cancelAtPeriodEnd ? null : (
        <Button size="sm" variant="outline" onClick={() => onChange(plan)} disabled={pendingSwitch !== null}>
          {t('billingPlans.switchTo', { tier: tierLabel(plan.tier) })}
        </Button>
      )
    }
    return (
      <Button size="sm" onClick={() => onSubscribe(plan)}>
        {t('billingPlans.subscribe')}
      </Button>
    )
  }

  return (
    <FormSection title={suite.suiteName}>
      <div className="space-y-6">
        {/* Where the suite stands. What is paid for comes first: a trial
            running alongside a subscription is a footnote, not the story. */}
        {paying ? (
          <div className="flex items-center justify-between rounded-lg border border-border bg-muted/40 p-4">
            <div>
              <h3 className="text-lg font-semibold text-foreground">
                {t('billingPlans.planName', { suite: suite.suiteName, tier: tierLabel(subscription!.tier) })}
              </h3>
              <p className="text-sm text-muted-foreground">
                {[
                  subscription?.seats != null ? t('billingPlans.seats', { count: subscription.seats }) : null,
                  subscription?.cancelAtPeriodEnd ? t('billingPlans.cancelsAtPeriodEnd') : null,
                  onGrant && suite.tier !== subscription!.tier
                    ? t(
                        suite.grant?.kind === 'extension'
                          ? 'billingPlans.extensionAlsoRunning'
                          : 'billingPlans.trialAlsoRunning',
                        { count: suite.grant?.daysRemaining }
                      )
                    : null,
                ]
                  .filter(Boolean)
                  .join(' · ')}
              </p>
            </div>
            <div className="flex items-center gap-2 text-green-600 dark:text-green-400">
              <CheckCircle2 className="h-4 w-4" />
              <span className="text-sm font-medium">{t('billing.active')}</span>
            </div>
          </div>
        ) : onGrant ? (
          <div className="rounded-lg border border-amber-200 bg-amber-50 p-4 dark:border-amber-800 dark:bg-amber-900/20">
            <div className="mb-2 flex items-center gap-2">
              <Clock className="h-5 w-5 text-amber-600 dark:text-amber-400" />
              <h3 className="font-semibold text-amber-800 dark:text-amber-200">
                {t(
                  suite.grant?.kind === 'extension' ? 'billingPlans.extensionHeading' : 'billingPlans.trialHeading',
                  { count: suite.grant?.daysRemaining }
                )}
              </h3>
            </div>
            <p className="text-sm text-amber-700 dark:text-amber-300">
              {t('billingPlans.trialBody')}
            </p>
          </div>
        ) : (
          <div className="flex flex-col gap-3 rounded-lg border border-border bg-muted/40 p-4 sm:flex-row sm:items-center sm:justify-between">
            <div>
              <h3 className="text-lg font-semibold text-foreground">
                {t('billingPlans.planName', { suite: suite.suiteName, tier: tierLabel('free') })}
              </h3>
              <p className="text-sm text-muted-foreground">
                {suite.grant && !suite.grant.live
                  ? `${t(suite.grant.kind === 'extension' ? 'billingPlans.extensionEnded' : 'billingPlans.trialEnded')} `
                  : ''}
                {t('billingPlans.everyFeatureNoAi')}
              </p>
            </div>
            {suite.trialAvailable && canBuy && (
              <Button onClick={onStartTrial} disabled={startingTrial}>
                <Sparkles className="mr-2 h-4 w-4" />
                {startingTrial ? t('billingPlans.startingTrial') : t('billingPlans.startTrial')}
              </Button>
            )}
          </div>
        )}

        {/* Only a paying organization has subscription details to miss. */}
        {paying && suite.billingProviderUnavailable && (
          <p className="text-sm text-muted-foreground">{t('billingPlans.stripeDown')}</p>
        )}

        <SeatMeter suite={suite} paying={paying && !stripeDown} canBuy={canBuy} onChangeSeats={onChangeSeats} t={t} />
        {/* The seat hint sits with the meter: one sentence on what a seat is and how it is charged. */}
        {paying && !stripeDown && canBuy && suite.seatsBought != null && (
          <p className="-mt-4 text-xs text-muted-foreground">{t('billingPlans.seatsHint')}</p>
        )}

        <CreditMeter suite={suite} t={t} />

        {/* A tier change Stripe accepted, until the webhook records it. The
            slow case is not an error: the change is not lost, our record lags. */}
        {pendingSwitch && (
          <div className="flex items-start gap-3 rounded-lg border border-border bg-primary/5 p-4" role="status">
            <Clock className={`mt-0.5 h-5 w-5 ${pendingSwitch.slow ? 'text-muted-foreground' : 'animate-pulse text-primary'}`} />
            <p className="flex-1 text-sm text-foreground">
              {t(pendingSwitch.slow ? 'billingPlans.switchSlow' : 'billingPlans.switchPending', { plan: pendingSwitch.plan.name })}
            </p>
            {pendingSwitch.slow && (
              <Button variant="outline" size="sm" onClick={onDismissPendingSwitch}>
                {t('billingPlans.dismiss')}
              </Button>
            )}
          </div>
        )}

        {/* The four tiers. The buttons hide, not disable, when the role is
            unknown or cannot buy; each case says why in its own words. */}
        <RoleUnknownNotice />
        {askAdmin && <p className="text-sm text-muted-foreground">{t('billingPlans.askAdmin')}</p>}
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead>
              <tr className="border-b border-border">
                <th className="w-56 px-4 py-3 text-left text-sm font-medium text-muted-foreground">{t('billingPlans.featureColumn')}</th>
                {plans.map((plan) => (
                  <th key={plan.slug} className="min-w-[150px] px-4 py-3 text-center">
                    <div className="space-y-1">
                      <div className="text-base font-semibold text-foreground">{tierLabel(plan.tier)}</div>
                      <div className="text-sm text-muted-foreground">{priceLabel(plan, t)}</div>
                      <div className="text-xs text-muted-foreground">{plan.description}</div>
                      {(plan.tier === currentTier || (onGrant && plan.tier === suite.tier && suite.source === 'grant')) && (
                        <span className="inline-flex items-center rounded-full bg-primary/15 px-2.5 py-0.5 text-xs font-medium text-primary dark:bg-primary/25 dark:text-[hsl(var(--primary-hover))]">
                          {plan.tier === currentTier
                            ? currentPlanLabel
                            : t(suite.grant?.kind === 'extension' ? 'billingPlans.onExtension' : 'billingPlans.onTrial')}
                        </span>
                      )}
                    </div>
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {featureKeys.map((key) => (
                <tr key={key} className="border-b border-border last:border-0">
                  <td className="px-4 py-3 text-sm font-medium text-foreground">{t(`billingPlans.feature_${key}`, { defaultValue: key })}</td>
                  {plans.map((plan) => {
                    const value = valueOf(plan, key)
                    return (
                      <td key={plan.slug} className="px-4 py-3 text-center text-sm text-foreground">
                        {typeof value === 'boolean' ? (
                          value ? (
                            <Check className="mx-auto h-4 w-4 text-green-600 dark:text-green-400" />
                          ) : (
                            <X className="mx-auto h-4 w-4 text-muted-foreground" />
                          )
                        ) : (
                          <span>{formatFeatureValue(value, t)}</span>
                        )}
                      </td>
                    )
                  })}
                </tr>
              ))}
            </tbody>
            <tfoot>
              <tr>
                <td className="px-4 py-4" />
                {plans.map((plan) => (
                  <td key={plan.slug} className="px-4 py-4 text-center">
                    {action(plan)}
                  </td>
                ))}
              </tr>
            </tfoot>
          </table>
        </div>
      </div>
    </FormSection>
  )
}
